import logger from "@/lib/logger";
import type { SegmentProductViewItemType } from "./utils";
import { sendEvent } from "./sender";

/**
 * Product Viewed properties for `edx.bi.ecommerce.product_viewed`.
 * Keys match the Segment payload contract (product_type, product_id, …).
 */
export type ProductViewedSegmentParams = {
  product_type: SegmentProductViewItemType;
  product_id: string;
  name: string;
  sku?: string;
  category?: string;
  brand?: string;
  currency?: string;
  price?: number;
  sale_price?: number;
  coupon?: string;
  /** Line of business. Defaults to `edx` for marketplace PDPs. */
  lob?: string;
  /** Defaults to `window.location.href` in the browser. */
  url?: string;
  image_url?: string;
};

function compactPayload(
  payload: Record<string, string | number | undefined>,
): Record<string, string | number> {
  return Object.fromEntries(
    Object.entries(payload).filter(
      ([, v]) => v !== undefined && v !== null && v !== "",
    ),
  ) as Record<string, string | number>;
}

function absolutizeMediaUrl(href: string | undefined): string | undefined {
  if (!href?.trim()) return undefined;
  const t = href.trim();
  if (t.startsWith("http://") || t.startsWith("https://")) return t;
  if (typeof window === "undefined") return t;
  try {
    return new URL(t, window.location.origin).href;
  } catch {
    return t;
  }
}

/**
 * Segment `analytics.track("edx.bi.ecommerce.product_viewed", …)`
 */
export function sendProductViewed(params: ProductViewedSegmentParams) {
  if (!params.product_type || !params.product_id || !params.name) {
    logger.warn(
      "Missing required data to send Segment product view event",
      params,
    );
    return;
  }

  const url =
    params.url ??
    (typeof window !== "undefined" ? window.location.href : undefined);
  const imageUrl = absolutizeMediaUrl(params.image_url);

  const raw: Record<string, string | number | undefined> = {
    product_type: params.product_type,
    product_id: params.product_id,
    sku: params.sku,
    category: params.category,
    name: params.name,
    brand: params.brand,
    currency: params.currency,
    price: params.price,
    sale_price: params.sale_price,
    coupon: params.coupon,
    lob: params.lob ?? "edx",
    url,
    image_url: imageUrl,
  };

  sendEvent("edx.bi.ecommerce.product_viewed", compactPayload(raw));
}
